/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

/**
 * Detail rows for the Univen property attributes (plan §3.1). Every row is
 * left out when its field is empty, the same as the rows beside it on the
 * detail page; a row is never shown as "not informed".
 */

import type {
  AuctionData,
  PropertyCharacteristics,
  PropertyCondominium,
  PropertyLocation,
  PropertyOwnership,
  ShortTermData,
} from '@services/housien/types-property';

/** One label/value line of a detail card, already translated. */
export interface AttributeRow {
  labelKey: string;
  value: string;
}

/** The translator the rows need: plain keys plus interpolation. */
export type RowTranslate = (key: string, options?: Record<string, unknown>) => string;

type Maybe<T> = T | null | undefined;

/**
 * Formats money the way the detail's price strip does (pt-BR, no cents).
 *
 * @param value - The amount.
 * @returns "R$ 600.000", or null when there is no positive amount.
 */
export function formatMoney(value: Maybe<number>): string | null {
  if (typeof value !== 'number' || !(value > 0)) return null;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

/**
 * Formats a stored ISO date (YYYY-MM-DD) as DD/MM/YYYY without a time zone
 * shift (a Date object would move it a day back west of UTC).
 *
 * @param iso - The stored date.
 * @returns The Brazilian date, the input when it is not ISO, or null when empty.
 */
export function formatIsoDate(iso: Maybe<string>): string | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}

/**
 * Whether a text holds something.
 *
 * @param value - The stored text.
 * @returns True when it is a non-blank string.
 */
function hasText(value: Maybe<string>): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Appends a row when the value is present.
 *
 * @param rows - Rows being built.
 * @param labelKey - The row label.
 * @param value - The display value, null when absent.
 */
function push(rows: AttributeRow[], labelKey: string, value: string | null | undefined): void {
  if (value) rows.push({ labelKey, value });
}

/**
 * Joins the informed parts with ", " ("Bloco B, apto 11").
 *
 * @param parts - Translated parts, null for the absent ones.
 * @returns The joined text, or null when nothing is informed.
 */
function joinParts(parts: Array<string | null>): string | null {
  const present = parts.filter((p): p is string => Boolean(p));
  return present.length ? present.join(', ') : null;
}

/**
 * A labelled part ("Bloco B"), or null when its value is empty.
 *
 * @param t - Translator.
 * @param key - The i18n key taking `{{value}}`.
 * @param value - The stored text.
 * @returns The part, or null.
 */
function part(t: RowTranslate, key: string, value: Maybe<string>): string | null {
  return hasText(value) ? t(key, { value: value.trim() }) : null;
}

const ENUM_ROWS: ReadonlyArray<readonly [keyof PropertyCharacteristics, string, string]> = [
  ['condition', 'detailView.terms.condition', 'form.options.condition'],
  ['occupancy', 'detailView.terms.occupancy', 'form.options.occupancy'],
  ['finish_standard', 'detailView.terms.finishStandard', 'form.options.finishStandard'],
  ['location_quality', 'detailView.terms.locationQuality', 'form.options.locationQuality'],
];

/**
 * Condition, occupancy, finish standard and location quality, translated.
 *
 * @param characteristics - The characteristics block.
 * @param t - Translator.
 * @returns The rows for the "Condições" card.
 */
export function collectConditionRows(characteristics: Maybe<PropertyCharacteristics>, t: RowTranslate): AttributeRow[] {
  const rows: AttributeRow[] = [];
  for (const [field, labelKey, optionPrefix] of ENUM_ROWS) {
    const key = characteristics?.[field];
    if (typeof key === 'string' && key) rows.push({ labelKey, value: t(`${optionPrefix}.${key}`, { defaultValue: key }) });
  }
  return rows;
}

/**
 * The Temporada card: nightly price, guests and cleaning fee.
 *
 * @param shortTerm - The `pricing.short_term` block.
 * @param t - Translator.
 * @returns The rows, empty when nothing is informed.
 */
export function collectShortTermRows(shortTerm: Maybe<ShortTermData>, t: RowTranslate): AttributeRow[] {
  const rows: AttributeRow[] = [];
  push(rows, 'detailView.shortTerm.price', formatMoney(shortTerm?.price));
  const guests = shortTerm?.guests;
  if (typeof guests === 'number' && guests > 0) {
    rows.push({ labelKey: 'detailView.shortTerm.guests', value: t('detailView.shortTerm.guestsValue', { count: guests }) });
  }
  push(rows, 'detailView.shortTerm.cleaningFee', formatMoney(shortTerm?.cleaning_fee));
  return rows;
}

/**
 * One auction round: "12/12/2026 · R$ 600.000", or whichever half is informed.
 *
 * @param date - The round's ISO date.
 * @param price - The round's value.
 * @returns The round line, or null when neither is informed.
 */
function auctionRound(date: Maybe<string>, price: Maybe<number>): string | null {
  const parts = [formatIsoDate(date), formatMoney(price)].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The Leilão card: date and value of the first and second rounds.
 *
 * @param auction - The `pricing.auction` block.
 * @returns The rows, empty when nothing is informed.
 */
export function collectAuctionRows(auction: Maybe<AuctionData>): AttributeRow[] {
  const rows: AttributeRow[] = [];
  push(rows, 'detailView.auction.first', auctionRound(auction?.first_date, auction?.first_price));
  push(rows, 'detailView.auction.second', auctionRound(auction?.second_date, auction?.second_price));
  return rows;
}

/**
 * The distance to the sea, in metres.
 *
 * @param location - The location block.
 * @returns "300 m", or null when not informed.
 */
export function formatDistanceToSea(location: Maybe<PropertyLocation>): string | null {
  const metres = location?.distance_to_sea_m;
  if (typeof metres !== 'number' || metres < 0) return null;
  return `${metres.toLocaleString('pt-BR')} m`;
}

/**
 * The "Condomínio e unidade" rows of the location card.
 *
 * @param location - The location block (block, unit, quadra, lot).
 * @param condominium - The condominium block (name, development, developer).
 * @param towerName - `category_specifics.tower_name`.
 * @param t - Translator.
 * @returns The rows, empty when nothing is informed.
 */
export function collectCondominiumUnitRows(
  location: Maybe<PropertyLocation>,
  condominium: Maybe<PropertyCondominium>,
  towerName: Maybe<string>,
  t: RowTranslate,
): AttributeRow[] {
  const rows: AttributeRow[] = [];
  push(rows, 'detailView.location.condominium', hasText(condominium?.name) ? condominium.name : null);
  push(rows, 'detailView.location.tower', hasText(towerName) ? towerName : null);
  push(rows, 'detailView.location.blockUnit', joinParts([
    part(t, 'detailView.location.blockValue', location?.block),
    part(t, 'detailView.location.unitValue', location?.unit),
  ]));
  push(rows, 'detailView.location.quadraLot', joinParts([
    part(t, 'detailView.location.quadraValue', location?.quadra),
    part(t, 'detailView.location.lotValue', location?.lot),
  ]));
  push(rows, 'detailView.location.development', hasText(condominium?.development_name) ? condominium.development_name : null);
  push(rows, 'detailView.location.developer', hasText(condominium?.developer) ? condominium.developer : null);
  return rows;
}

/**
 * A yes/no answer with its deadline: "Sim, até 30/06/2027", "Sim", "Não".
 *
 * @param flag - The stored answer, absent when not informed.
 * @param until - The ISO deadline.
 * @param t - Translator.
 * @returns The text, or null when not informed.
 */
function flagWithDeadline(flag: Maybe<boolean>, until: Maybe<string>, t: RowTranslate): string | null {
  if (flag === false) return t('detailView.terms.no');
  if (flag !== true) return null;
  const date = formatIsoDate(until);
  return date ? t('detailView.ownership.yesUntil', { date }) : t('detailView.terms.yes');
}

/**
 * Exclusivity, publishing authorization, key location and partner. Shown
 * beside the mandate rows, which keep their own exclusive type (plan §12).
 *
 * @param ownership - The ownership block.
 * @param t - Translator.
 * @returns The rows, empty when nothing is informed.
 */
export function collectOwnershipAttributeRows(ownership: Maybe<PropertyOwnership>, t: RowTranslate): AttributeRow[] {
  const rows: AttributeRow[] = [];
  push(rows, 'detailView.ownership.exclusive', flagWithDeadline(ownership?.exclusive, ownership?.exclusive_until, t));
  push(rows, 'detailView.ownership.publishAuthorized',
    flagWithDeadline(ownership?.publish_authorized, ownership?.publish_authorized_until, t));
  push(rows, 'detailView.ownership.keyLocation', hasText(ownership?.key_location) ? ownership.key_location : null);
  push(rows, 'detailView.ownership.partner', hasText(ownership?.partner_name) ? ownership.partner_name : null);
  return rows;
}
